import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Layout({ children, user }) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Employees may use the inventory tool but not view financial data.
  const canViewFinancials = user?.role === 'admin'

  const isActive = (path) => {
    return location.pathname === path ? 'border-b-2 border-black' : ''
  }

  const handleLogout = async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch('/logout', {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout failed:', error)
      // Force redirect anyway
      window.location.href = '/login'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center">
                  <img
                    className="h-8 w-auto"
                    src="/wv_logo.png"
                    alt="Weekend Vans"
                  />
                  <span className="ml-3 text-xl font-bold text-gray-900">
                    Weekend Vans
                  </span>
                </Link>
              </div>
              {/* Desktop Navigation (hidden below 750px) */}
              <div className="hidden md:ml-8 md:flex md:space-x-8">
                {canViewFinancials && (
                  <>
                    <Link
                      to="/"
                      className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 ${isActive('/')}`}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/vans"
                      className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-gray-900 ${isActive('/vans')}`}
                    >
                      Vans
                    </Link>
                    <Link
                      to="/van-sales"
                      className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-gray-900 ${isActive('/van-sales')}`}
                    >
                      Van Sales
                    </Link>
                  </>
                )}
                <Link
                  to="/inventory"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-gray-900 ${isActive('/inventory')}`}
                >
                  Inventory
                </Link>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Hamburger Menu Button (visible below 750px) */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {/* Hamburger icon */}
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>

              {user && (
                <>
                  <span className="text-sm text-gray-700">
                    {user.name}
                    {user.admin && (
                      <Link
                        to="/admin"
                        className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 inline-block"
                      >
                        Admin
                      </Link>
                    )}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu, show/hide based on menu state (visible below 750px) */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 pt-2 space-y-1">
              {canViewFinancials && (
                <>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      location.pathname === '/'
                        ? 'border-black bg-gray-50 text-gray-900'
                        : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/vans"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      location.pathname === '/vans'
                        ? 'border-black bg-gray-50 text-gray-900'
                        : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    Vans
                  </Link>
                  <Link
                    to="/van-sales"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                      location.pathname === '/van-sales'
                        ? 'border-black bg-gray-50 text-gray-900'
                        : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    Van Sales
                  </Link>
                </>
              )}
              <Link
                to="/inventory"
                onClick={() => setMobileMenuOpen(false)}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                  location.pathname === '/inventory'
                    ? 'border-black bg-gray-50 text-gray-900'
                    : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                Inventory
              </Link>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  )
}
